import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { DialogProps, StyleClasses } from '@paintscout/ui'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Grid,
  InputField,
  FormSection,
  FormSectionTitle,
  IconButton,
  SortableTiles
} from '@paintscout/ui'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import type { OptionsDocument, Pipeline } from 'paintscout'
import { updatePipeline, uuid } from '@paintscout/util/builder'
import clone from 'lodash/clone'
import Add from '@material-ui/icons/Add'
import { arrayMove, SortableElement } from 'react-sortable-hoc'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { motion } from 'framer-motion'
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      position: 'relative',
      display: 'grid',
      gridGap: '0.75em',
      justifyContent: 'space-between',
      gridTemplateColumns: `repeat(1, 1fr)`
    },
    dragging: {
      zIndex: 2147483002
    },
    dialogTitle: {},
    dialogContent: {},
    stageBody: {
      background: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      boxShadow: 'none',
      height: '100%'
    },
    listBody: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      height: '100%',
      gap: theme.spacing(2)
    },
    tiles: {},
    tile: {},
    stageLabel: {},
    addStageButtonWrapper: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(7.5)
    },

    dragIcon: {
      color: theme.paintscout.palette.brand.Neutral.P90,
      cursor: 'grab',
      alignSelf: 'flex-end',
      fontSize: '2.5rem',
      margintRight: theme.spacing(1)
    },
    removeButton: {
      marginTop: theme.spacing(2.5),
      marginLeft: theme.spacing(2.5),
      '&:hover': {
        color: theme.palette.error.main
      }
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'PipelineDialog' }
)

export interface PipelineDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  options: OptionsDocument
  pipeline: Pipeline
  isNew: boolean
  onConfirm: (updatedOptions: OptionsDocument, isDirty: boolean) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
}

export const maxStages = 15

function PipelineDialog(props: PipelineDialogProps) {
  const classes = useStyles()
  const { onConfirm, onCancel, options, pipeline, isNew, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const [isDirty, setIsDirty] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPipeline, setCurrentPipeline] = useState(clone(pipeline))

  const leftButton = (
    <Button className={classes.cancel} disabled={isLoading} variant={'text'} onClick={onCancel}>
      {'Cancel'}
    </Button>
  )

  function handleAddStage() {
    const newStage = {
      key: uuid(),
      label: ''
    }
    setCurrentPipeline((prev) => ({
      ...prev,
      stages: [...prev.stages, newStage]
    }))
    setIsDirty(true)
  }

  function handleRemoveStage(key: string) {
    const updatedStages = currentPipeline.stages.filter((stage) => stage.key !== key)
    setCurrentPipeline((prev) => ({
      ...prev,
      stages: updatedStages
    }))
    setIsDirty(true)
  }

  console.log('PipelineDialog', { pipeline, isNew, isDirty, currentPipeline })
  function shouldCancelStart(event: any) {
    // If the user is clicking on the drag-indicator (or any child of the drag-indicator), don't cancel the start
    return !event?.target?.closest('#drag-indicator')
  }

  function handleReorder({ oldIndex, newIndex }: any) {
    if (oldIndex === newIndex) {
      return
    }
    const newOrder = arrayMove([...currentPipeline.stages], oldIndex, newIndex)
    setCurrentPipeline((prev) => ({
      ...prev,
      stages: newOrder
    }))
    setIsDirty(true)
  }

  return (
    <Dialog {...baseDialogProps}>
      <DialogTitle classes={{ root: classes.dialogTitle }}>
        <Typography variant="h2">{isNew ? 'Create' : 'Edit'} Pipeline</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form
          id="pipeline-option"
          onSubmit={(ev) => {
            ev.preventDefault()
            handleSubmit()
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InputField
                classes={{ root: classes.input }}
                label={'Name'}
                name={'label'}
                value={currentPipeline.label}
                fullWidth={true}
                onChange={handleInputChange}
                required
                error={!currentPipeline.label}
              />
            </Grid>
            <Grid item xs={12}>
              <PipelineForm
                currentPipeline={currentPipeline}
                handleStageNameChange={handleStageNameChange}
                handleAddStage={handleAddStage}
                handleRemoveStage={handleRemoveStage}
                handleReorder={handleReorder}
                shouldCancelStart={shouldCancelStart}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions leftButton={leftButton}>
        <Button
          className={classes.confirm}
          type="submit"
          form="pipeline-option"
          variant={'contained'}
          disabled={isLoading}
          data-testid="pipeline-dialog-confirm-button"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      const updatedOptions = updatePipeline({ options, pipeline: currentPipeline })
      onConfirm(updatedOptions, isDirty)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }

  function handleInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = event.target
    setCurrentPipeline((prev) => ({
      ...prev,
      [name]: value
    }))
    setIsDirty(true)
  }

  function handleStageNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = event.target
    const id = name.match(/\[(.*?)\]/)?.[1]
    const stageIndex = currentPipeline.stages.findIndex((stage) => stage.key === id)
    if (stageIndex === -1) {
      console.error('Stage not found')
      return
    }
    const updatedStage = {
      ...currentPipeline.stages[stageIndex],
      label: value
    }
    const updatedStages = [...currentPipeline.stages]
    updatedStages[stageIndex] = updatedStage
    setCurrentPipeline((prev) => ({
      ...prev,
      stages: updatedStages
    }))

    setIsDirty(true)
  }
}

export default PipelineDialog

const itemVariants = {
  hidden: { opacity: 0, transition: { duration: 0.2 } },
  show: { opacity: 1 }
}

interface SortableStageProps {
  name: string
  value: string
  handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleRemove: (key: string) => void
  showSort: boolean
  id: string
}
export const SortableStage: React.ComponentClass<any, any> = SortableElement(function SortableTile({
  name,
  value,
  handleChange,
  handleRemove,
  showSort,
  id
}: SortableStageProps & { disableTile?: boolean }) {
  const classes = useStyles()

  // wrap the tile in a div so that the transitions from drag & drop don't
  // conflict with MotionTile
  return (
    <div>
      <motion.div style={{ height: '100%' }} variants={itemVariants} layout={undefined} transition={undefined}>
        <div key={id} id={id} className={classes.stageBody}>
          {showSort && (
            <DragIndicatorIcon onClick={(e) => e.stopPropagation()} id="drag-indicator" className={classes.dragIcon} />
          )}
          <InputField
            classes={{ root: classes.stageLabel }}
            label={'Name'}
            placeholder="Stage"
            name={name}
            value={value}
            fullWidth
            onChange={handleChange}
            required
            error={!value}
          />
          {showSort && (
            <IconButton size="small" className={classes.removeButton} onClick={() => handleRemove(id)}>
              <RemoveCircleIcon />
            </IconButton>
          )}
        </div>
      </motion.div>
    </div>
  )
})

export const PipelineForm = ({
  currentPipeline,
  handleStageNameChange,
  handleAddStage,
  handleRemoveStage,
  handleReorder,
  shouldCancelStart,
  isLoading
}: {
  currentPipeline: Pipeline
  handleStageNameChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  handleAddStage: () => void
  handleRemoveStage: (key: string) => void
  handleReorder: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void
  shouldCancelStart: (event: any) => boolean
  isLoading: boolean
}) => {
  const classes = useStyles()

  return (
    <FormSection>
      <FormSectionTitle variant="body1" title={'Stages (in order)'} />
      <div className={classes.listBody}>
        <SortableTiles
          axis={'y'}
          helperClass={classes.dragging}
          pressDelay={150}
          shouldCancelStart={shouldCancelStart}
          onSortEnd={handleReorder}
          className={classes.root}
        >
          {currentPipeline.stages.map((stage, index) => (
            <SortableStage
              label={'Name'}
              index={index}
              placeholder="Stage"
              name={`stages[${stage?.key}].label`}
              value={stage?.label}
              handleChange={handleStageNameChange}
              error={!stage?.label}
              id={stage.key}
              key={index}
              showSort={currentPipeline.stages.length > 1}
              className={classes.tile}
              handleRemove={handleRemoveStage}
            />
          ))}
        </SortableTiles>
        {currentPipeline.stages.length < maxStages ? (
          <div className={currentPipeline.stages.length > 1 ? classes.addStageButtonWrapper : ''}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleAddStage}
              data-testid="add-stage-button"
              disabled={isLoading}
              icon={Add}
            >
              Add Stage
            </Button>
          </div>
        ) : (
          <Typography variant="subtitle2" align="center">
            Maximum of {maxStages} stages reached
          </Typography>
        )}
      </div>
    </FormSection>
  )
}
